/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {useAuthenticatedUser} from '@ion/applications/src/pages/admin/users/useAuthenticatedUser';
import {GenericObject} from '@ion/core/src/types/entities';
import {Operation} from '@ion/core/src/types/general';
import {useUserPermissions} from '@ion/query/src/hooks/useUserPermissions';
import {getFilePermissionsPermission} from '../helpers';
import useFilePermissionsSearchFilter from './useFilePermissionsSearchFilter';

/**
 * Provides a utility hook to check the current user role has tagged
 * any one of the CREATE/UPDATE/READ/ASSIGN FILE PERMISSIONS feature
 * enabled to filter device/file types.
 *
 * ```typescript
 *
 * import {useFilePermissionsSearch} from '@ion/applications/src/hooks/';
 *
 * const hasReadFilePermissions = useFilePermissionsSearch();
 *
 * ```
 *
 */
export const useFilePermissionsSearch = (Features: GenericObject) => {
  const currentUser     = useAuthenticatedUser();
  const userPermissions = useUserPermissions(!!currentUser?.id)?.userPermissions ?? [];
  const filePermissions = useFilePermissionsSearchFilter(Features);
  const filePermission  = getFilePermissionsPermission(filePermissions);
  const permissions     = userPermissions?.filter?.(item =>
    filePermission?.includes?.(
      item?.permission?.resource
    ));
  const permission = permissions?.filter?.(
    item => item?.permission?.action !== Operation.Delete
  );
  return Array.from(
    new Set(permission?.map(({permission}) => permission.resource))
  );
};

export default useFilePermissionsSearch;
